import { useMemo } from 'react';

import type { ServiceContract } from '@cognite/apm-client';
import { UserPreferencesService, UserService } from '@cognite/apm-client';
import { useAuthContext } from '@cognite/e2e-auth';
import { FDMClient } from '@cognite/fdm-client';

import {
  USER_INSTANCE_SPACE,
  USER_MODEL_NAME,
  USER_MODEL_SPACE,
  USER_MODEL_VERSION,
  USER_PREFERENCES_VIEW_NAME,
  USER_VIEW_NAME,
} from '../../../constants/cdm-infield-data-model';

/**
 * Creates and memoizes user-related FDM services.
 * This hook centralizes the creation of UserService and UserPreferencesService
 * to avoid duplicate service instantiation across the app.
 */
export const useUserServices = () => {
  const { client } = useAuthContext();

  const fdmClient = useMemo(() => new FDMClient(client), [client]);

  const userServiceContract: ServiceContract = useMemo(
    () => ({
      modelSpace: USER_MODEL_SPACE,
      modelName: USER_MODEL_NAME,
      instanceSpace: USER_INSTANCE_SPACE,
      modelVersion: USER_MODEL_VERSION,
      fdmClient: fdmClient,
    }),
    [fdmClient]
  );

  const userService = useMemo(
    () =>
      new UserService(userServiceContract, client, {
        userViewName: USER_VIEW_NAME,
      }),
    [userServiceContract, client]
  );

  const userPreferencesService = useMemo(
    () =>
      new UserPreferencesService(userServiceContract, {
        userPreferencesViewName: USER_PREFERENCES_VIEW_NAME,
      }),
    [userServiceContract]
  );

  return {
    fdmClient,
    userServiceContract,
    userService,
    userPreferencesService,
    userInstanceSpace: USER_INSTANCE_SPACE,
  };
};
